import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartIcon from "@mui/icons-material/BarChart";
import BrushIcon from "@mui/icons-material/Brush";
import BuildIcon from "@mui/icons-material/Build";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { Suspense, useMemo } from "react";
import { useLazyLoadQuery } from "react-relay";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { InternBrandQuery } from "./__generated__/InternBrandQuery.graphql";
import nullthrows from "../../utils/nullthrows";
import InternBrandAdsHealth from "./InternBrandAdsHealth";
import InternBrandBilling from "./InternBrandBilling";
import InternBrandBuilds from "./InternBrandBuilds";
import InternBrandAdsAnalysis from "./ads/InternBrandAdsAnalysis";
import InternContentGenContainer from "./gen/InternContentGenContainer";
import InternBrandOnboarding from "./onboarding/InternBrandOnboarding";
import InternBrandDashboard from "./overview/InternBrandDashboard";
import InternBrandStyle from "./style/InternBrandStyle";

const query = graphql`
  query InternBrandQuery($id: ID!) {
    brand(id: $id) {
      id
      displayName
      appConfig {
        appState
      }
      ...InternBrandBilling_brand
      ...InternBrandBuilds_brand
      ...InternBrandDashboard_brand
      ...InternBrandOnboarding_brand
    }
  }
`;

interface TabConfig {
  label: string;
  shortLabel: string;
  path: string;
  icon: React.ReactElement;
  component: (brand: InternBrandQuery["response"]["brand"]) => React.ReactNode;
}

const TABS: TabConfig[] = [
  {
    label: "Overview",
    shortLabel: "Overview",
    path: "/overview",
    icon: <DashboardIcon fontSize="small" />,
    component: (brand) => <InternBrandDashboard brand={brand} />,
  },
  {
    label: "Billing",
    shortLabel: "Billing",
    path: "/billing",
    icon: <MonetizationOnIcon fontSize="small" />,
    component: (brand) => <InternBrandBilling brand={brand} />,
  },
  {
    label: "Onboarding",
    shortLabel: "Onboard",
    path: "/onboarding",
    icon: <PlayCircleOutlineIcon fontSize="small" />,
    component: (brand) => <InternBrandOnboarding brand={brand} />,
  },
  {
    label: "Builds",
    shortLabel: "Builds",
    path: "/builds",
    icon: <BuildIcon fontSize="small" />,
    component: (brand) => <InternBrandBuilds brand={brand} />,
  },
  {
    label: "Style",
    shortLabel: "Style",
    path: "/style",
    icon: <BrushIcon fontSize="small" />,
    component: (brand) => <InternBrandStyle brandID={brand.id} />,
  },
  {
    label: "Ads Health",
    shortLabel: "Ads",
    path: "/ads_health",
    icon: <TrendingUpIcon fontSize="small" />,
    component: (brand) => <InternBrandAdsHealth brandID={brand.id} />,
  },
  {
    label: "Ads Analysis",
    shortLabel: "Analysis",
    path: "/ads_analysis",
    icon: <BarChartIcon fontSize="small" />,
    component: (brand) => <InternBrandAdsAnalysis brandID={brand.id} />,
  },
  {
    label: "Content Generation",
    shortLabel: "Content",
    path: "/generation",
    icon: <TextFieldsIcon fontSize="small" />,
    component: (brand) => <InternContentGenContainer brandID={brand.id} />,
  },
];

const APP_STATE_CONFIG: Record<
  string,
  { label: string; color: "success" | "warning" | "error" | "default" | "info" }
> = {
  PRODUCTION: { label: "Production", color: "success" },
  PRE_PRODUCTION: { label: "Pre-Production", color: "info" },
  PENDING_KINN_SETUP: { label: "Pending Kinn Setup", color: "error" },
  PENDING_BRAND_SETUP: { label: "Pending Brand Setup", color: "warning" },
  DISABLED: { label: "Disabled", color: "default" },
};

const InternBrand = () => {
  const brandID = useParams().id;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const data = useLazyLoadQuery<InternBrandQuery>(query, {
    id: nullthrows(brandID),
  });
  const brand = data.brand;
  const appConfig = brand?.appConfig;
  const appState = appConfig?.appState ?? "DISABLED";
  const stateConfig = APP_STATE_CONFIG[appState] ?? APP_STATE_CONFIG.DISABLED;

  const basePath = useMemo(() => `/intern/brands/${brandID}`, [brandID]);

  const selectedTab = useMemo(() => {
    const relativePath = location.pathname.replace(basePath, "");
    return TABS.find((tab) => tab.path === relativePath) || TABS[0];
  }, [location.pathname, basePath]);

  const handleTabChange = (_: React.SyntheticEvent, value: string) => {
    navigate(value);
  };

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.5, sm: 2, md: 2.5 },
          bgcolor: "var(--intern-bg-primary)",
          border: "1px solid var(--intern-border-default)",
          borderRadius: 2,
        }}
      >
        {/* Breadcrumb */}
        <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
          <Tooltip title="Back to Brands">
            <IconButton
              size="small"
              onClick={() => navigate("/intern/brands")}
              sx={{
                color: "var(--intern-text-tertiary)",
                p: 0.5,
                "&:hover": {
                  color: "var(--intern-text-primary)",
                  bgcolor: "var(--intern-bg-hover)",
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Tooltip>
          <Breadcrumbs
            separator="›"
            sx={{
              "& .MuiBreadcrumbs-separator": {
                color: "var(--intern-text-tertiary)",
                mx: 0.5,
              },
            }}
          >
            <Link
              component="button"
              underline="hover"
              onClick={() => navigate("/intern/brands")}
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                "&:hover": { color: "var(--intern-text-secondary)" },
              }}
            >
              Brands
            </Link>
            <Typography
              variant="body2"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                maxWidth: { xs: 150, sm: "none" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {brand.displayName}
            </Typography>
          </Breadcrumbs>
        </Stack>

        {/* Brand Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={0.5}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 0.5, sm: 1.5 }}
          >
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{
                color: "var(--intern-text-primary)",
                fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
                wordBreak: "break-word",
              }}
            >
              {brand.displayName}
            </Typography>
            <Chip
              label={
                isSmall ? stateConfig.label.split(" ")[0] : stateConfig.label
              }
              size="small"
              color={stateConfig.color}
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.625rem", sm: "0.75rem" },
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Navigation Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          bgcolor: "var(--intern-bg-primary)",
          border: "1px solid var(--intern-border-default)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={`${basePath}${selectedTab.path}`}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: { xs: 40, sm: 44 },
            "& .MuiTab-root": {
              minHeight: { xs: 40, sm: 44 },
              py: { xs: 0.5, sm: 1 },
              px: { xs: 1, sm: 1.5, md: 2 },
              color: "var(--intern-text-secondary)",
              "&.Mui-selected": {
                color: "var(--intern-accent)",
              },
            },
            "& .MuiTabs-indicator": {
              bgcolor: "var(--intern-accent)",
            },
            "& .MuiTabs-scrollButtons": {
              width: { xs: 28, sm: 40 },
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.path}
              value={`${basePath}${tab.path}`}
              icon={isSmall ? undefined : tab.icon}
              iconPosition="start"
              label={isSmall ? tab.shortLabel : tab.label}
              sx={{
                textTransform: "none",
                fontWeight: selectedTab.path === tab.path ? 600 : 400,
                fontSize: { xs: "0.6875rem", sm: "0.75rem", md: "0.8125rem" },
                minWidth: "auto",
                "& .MuiTab-iconWrapper": {
                  mr: 0.5,
                },
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Content Area */}
      <Box>
        <Routes>
          {TABS.map((tab) => (
            <Route
              key={tab.path}
              path={tab.path}
              element={
                <Suspense fallback={<TabLoadingFallback />}>
                  {tab.component(brand)}
                </Suspense>
              }
            />
          ))}
        </Routes>
      </Box>
    </Box>
  );
};

const TabLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
      color: "var(--intern-text-tertiary)",
    }}
  >
    <CircularProgress size={24} color="inherit" />
  </Box>
);

export default InternBrand;
